import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ThemeService } from '../../service/theme.service';
import { IotService } from '../../service/iot.service';
import { ClientService } from '../../service/client.service';
import { FilterService } from '../../service/filter.service';
import { ReportsService } from '../../service/reports.service';
import { PdfService } from '../../service/pdf.service';
import { TranslateService } from '@ngx-translate/core';
import { CsvService } from '../../service/csv.service';
import { SystemMessageService } from '../../service/system-message.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-iot-reports-view',
  templateUrl: './iot-reports-view.component.html',
  styleUrls: ['./iot-reports-view.component.css'],
  standalone: false
})
export class IotReportsViewComponent implements OnInit, AfterViewInit {
  @ViewChild('usageCanvas') usageCanvas!: ElementRef;
  @ViewChild('marketCanvas') marketCanvas!: ElementRef;
  @ViewChild('countCanvas') countCanvas!: ElementRef;
  @ViewChild('tonnageCanvas') tonnageCanvas!: ElementRef;
  @ViewChild('usageTonnageCanvas') usageTonnageCanvas!: ElementRef;
  @ViewChild('marketTonnageCanvas') marketTonnageCanvas!: ElementRef;
  @ViewChild('subgroupTonnageCanvas') subgroupTonnageCanvas!: ElementRef;

  startDate: any = this.reports.getDateThirtyDaysAgo();
  endDate: any = this.reports.getCurrentDate();
  statistic: string = 'Collections';
  selectedThings: string[] = [];
  selectedMarkets: any = 'All';
  allThings: boolean = true;
  allMarkets: boolean = true;

  selectedClient: string = '';
  filterThingGroup: string = '';
  filterThingSubGroup: string = '';

  thingClient: any[] = [];
  clientSubGroups: any[] = [];
  originalClientSubGroups: any[] = [];

  things: any[] = [];
  originalThings: any[] = [];

  deviceList: any[] = [];
  originalDeviceList: any[] = [];

  history: any[] = [];
  counts: any[] = [];
  collections: any[] = [];

  generatingCSV = false;

  constructor(
    public theme: ThemeService,
    public iotService: IotService,
    private clientService: ClientService,
    private filter: FilterService,
    public reports: ReportsService,
    public pdf: PdfService,
    private translate: TranslateService,
    public csv: CsvService,
    public systemMessage: SystemMessageService
  ) {}

  async ngOnInit(): Promise<void> {
    await this.initData();
    this.getClientList();
  }

  ngAfterViewInit() {
    // wire canvases for PDF charts
    this.pdf.tickFontSize = 10;
    this.pdf.legendFontSize = 10;
    this.pdf.usageCanvas = this.usageCanvas;
    this.pdf.marketCanvas = this.marketCanvas;
    this.pdf.countCanvas = this.countCanvas;
    this.pdf.tonnageCanvas = this.tonnageCanvas;
    this.pdf.usageTonnageCanvas = this.usageTonnageCanvas;
    this.pdf.marketTonnageCanvas = this.marketTonnageCanvas;
    this.pdf.subgroupTonnageCanvas = this.subgroupTonnageCanvas;
  }

  get thingGroupOptions() {
    return [
      { label: 'allGroups', value: '' },
      { label: 'DEV', value: 'dev' },
      { label: 'POC', value: 'poc' },
      { label: 'PRD', value: 'prd' },
    ];
  }

  // get thingsOptions() {
  //   return (this.things || []).map((thing: any) => ({
  //     label: thing.name,
  //     value: thing.id,
  //   }));
  // }

  get marketSegmentOptions() {
    return [{ label: 'allMarkets', value: 'All' }];
  }

  get statisticOptions() {
    return [
      { label: this.translate.instant('collections'), value: 'Collections' },
    ];
  }

  async initData(): Promise<void> {
    this.iotService.resetDeviceArray?.();
    await this.clientService.getClientsForSelect();
    await this.iotService.adminDeviceArray().then(() => {
      const devices =
        (this.iotService.originalDevicesArray &&
          this.iotService.originalDevicesArray.length > 0
          ? this.iotService.originalDevicesArray
          : this.iotService.devicesArray) || [];
      this.originalDeviceList = this.iotService.devicesArray || [];
      this.deviceList = this.originalDeviceList;
      this.things = this.buildUniqueThings(devices);
      this.originalThings = [...this.things];
    });
  }

  getClientList() {
    try {
      this.clientService.getClients().subscribe((response: any) => {
        this.thingClient = response || [];
      });
    } catch (error) {
      console.error('Error getting client list: ', error);
    }
  }

  onChangeAllThings(event: any) {
    this.allThings = (event.target as HTMLInputElement).checked;
    return this.allThings;
  }

  onChangeAllMarkets(event: any) {
    this.allMarkets = (event.target as HTMLInputElement).checked;
    return this.allMarkets;
  }

  reportClientChange() {
    const filteredDevices = this.iotService.devicesArray || [];
    this.things = this.originalThings.filter((thing: any) =>
      filteredDevices.some((device: any) => device.thingName === thing.id)
    );
  }

  async onClientChange() {
    if (!this.selectedClient) {
      this.things = this.originalThings;
      return;
    }
    await this.getClientSubGroups();
    await this.applyDeviceFilters();
    this.reportClientChange();
  }

  async onGroupChange() {
    if (!this.filterThingGroup) {
      this.things = this.originalThings;
      return;
    }
    await this.applyDeviceFilters();
    this.reportClientChange();
  }

  async onSubGroupChange() {
    await this.applyDeviceFilters();
    this.reportClientChange();
  }

  async getClientSubGroups() {
    try {
      const response: any = await firstValueFrom(
        this.iotService.getClientSubGroups(this.selectedClient)
      );
      const subgroups = Array.isArray(response) ? response : [];
      this.originalClientSubGroups = subgroups;
      this.clientSubGroups = [...subgroups];
      this.clientSubGroups.forEach((subGroup: any) => {
        const thingNames = subGroup.devices.map((d: any) => d.thing_name);
        subGroup.matchedDevices = (this.iotService.devicesArray || []).filter(
          (device: any) => thingNames.includes(device.thingName)
        );
      });
    } catch (err) {
      console.error('Failed to load subgroups', err);
    }
  }

  async applyDeviceFilters() {
    let filtered = [...(this.iotService.originalDevicesArray || [])];

    if (this.selectedClient) {
      filtered = this.filter.search(
        this.selectedClient,
        filtered,
        this.originalDeviceList,
        'bin.client_id'
      );
    }

    if (this.filterThingGroup !== '') {
      const groupThingNames = await this.iotService.filterThingGroup(
        this.filterThingGroup
      );
      filtered = filtered.filter((device) =>
        groupThingNames.includes(device.thingName)
      );
    }

    if (this.filterThingSubGroup != null && this.filterThingSubGroup !== '') {
      const sg = this.clientSubGroups.find(
        (subGroup: any) => subGroup.sub_groupID === this.filterThingSubGroup
      );
      if (sg) {
        const thingNames = sg.devices.map((d: any) => d.thing_name);
        filtered = filtered.filter((device) =>
          thingNames.includes(device.thingName)
        );
      }
    }

    this.iotService.devicesArray = filtered;
    this.filterThingGroup = '';
  }

  resetDeviceFilters() {
    this.selectedClient = '';
    this.filterThingGroup = '';
    this.filterThingSubGroup = '';
    this.iotService.devicesArray = this.iotService.originalDevicesArray;
    this.things = this.originalThings;
  }

  dateToUnixTimestamp(dateString: string): number {
    const date = new Date(dateString);
    const timestampInMilliseconds = date.getTime();
    return Math.floor(timestampInMilliseconds / 1000);
  }

  async compileReport() {
    this.pdf.generatingPDF = true;
    try {
      const things: string[] = [];
      if (!this.allThings) {
        things.push(...this.selectedThings);
      } else {
        for (const row of this.things) {
          things.push(row.id);
        }
      }

      this.pdf.things = things;
      this.pdf.start = this.startDate;
      this.pdf.end = this.endDate;
      await this.pdf.savePDF();
    } catch (error) {
      this.pdf.generatingPDF = false;
    }
  }

  async exportCSVReports() {
    this.generatingCSV = true;

    this.history = [];
    this.counts = [];
    this.collections = [];
    const start = this.dateToUnixTimestamp(this.startDate + 'T00:00:00');
    const end = this.dateToUnixTimestamp(this.endDate + 'T23:59:59');

    let things: any[] = [];
    if (!this.allThings) {
      things = this.selectedThings;
    } else {
      for (const row of this.things) {
        things.push(row.id);
      }
    }

    try {
      const res = await this.reports.getReportData(things, start, end);

      for (const collection of JSON.parse(res?.collections)) {
        this.collections.push(collection);
      }

      for (const counts of JSON.parse(res?.counts)) {
        this.counts.push(counts);
      }

      this.history = await this.reports.getHistoryBatch(things, start, end);

      const deviceReport: any = [];
      const historyArray = this.history;

      for (const row of historyArray) {
        if (row.timestamp.toString().length <= 10) {
          row.timestamp = new Date(row.timestamp * 1000);
        } else {
          row.timestamp = new Date(parseInt(row.timestamp));
        }

        row.img = 'N/A';
        row.fdv_timestamp = new Date(row.fdv_timestamp * 1000);
        row.img_timestamp = new Date(row.img_timestamp * 1000);
        row.tmp_timestamp = new Date(row.tmp_timestamp * 1000);
        row.vcc_timestamp = new Date(row.vcc_timestamp * 1000);
        row.sig_timestamp = new Date(row.sig_timestamp * 1000);
        row.pre_timestamp = new Date(row.pre_timestamp * 1000);
        row.bat_timestamp = new Date(row.bat_timestamp * 1000);
        row.tm1_timestamp = new Date(row.tm1_timestamp * 1000);
        row.tm2_timestamp = new Date(row.tm2_timestamp * 1000);
        row.dst_timestamp = new Date(row.dst_timestamp * 1000);
        row.tm0_timestamp = new Date(row.tm0_timestamp * 1000);
        row.nct_timestamp = new Date(row.nct_timestamp * 1000);
        row.lfr_timestamp = new Date(row.lfr_timestamp * 1000);
        row.hum_timestamp = new Date(row.hum_timestamp * 1000);
        row.voc_timestamp = new Date(row.voc_timestamp * 1000);
        row.hib_timestamp = new Date(row.hib_timestamp * 1000);

        deviceReport.push(row);
      }

      this.csv.exportToCsv(
        deviceReport,
        `History-${this.startDate.replaceAll('-', '')}-${this.endDate.replaceAll('-', '')}.csv`
      );
      this.generatingCSV = false;
    } catch (error) {
      this.systemMessage.selectRibbon('danger', 'alert-danger-generic-message');
      console.error('CSV Error: ', error);
      this.generatingCSV = false;
    }
  }

  private buildUniqueThings(devices: any[]): any[] {
    const seen = new Set<string>();
    return (devices || [])
      .map((row: any) => ({ id: row.thingName, name: row.thingName }))
      .filter((item) => {
        if (!item.id || seen.has(item.id)) {
          return false;
        }
        seen.add(item.id);
        return true;
      });
  }
}
